import { useEffect } from 'react'

interface SEOHeadProps {
  title: string
  description: string
  keywords?: string
  canonical?: string
  ogImage?: string
  ogType?: 'website' | 'article'
  structuredData?: object
  noIndex?: boolean
}

export function SEOHead({ 
  title, 
  description, 
  keywords, 
  canonical, 
  ogImage = '/images/og-default.jpg',
  ogType = 'website',
  structuredData,
  noIndex = false
}: SEOHeadProps) {
  useEffect(() => {
    // Set document title
    document.title = title

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta')
    metaDescription.setAttribute('name', 'description')
    metaDescription.setAttribute('content', description)
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription)
    }

    // Set keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta')
      metaKeywords.setAttribute('name', 'keywords')
      metaKeywords.setAttribute('content', keywords)
      if (!document.querySelector('meta[name="keywords"]')) {
        document.head.appendChild(metaKeywords)
      }
    }

    // Set canonical URL
    if (canonical) {
      const linkCanonical = document.querySelector('link[rel="canonical"]') || document.createElement('link')
      linkCanonical.setAttribute('rel', 'canonical')
      linkCanonical.setAttribute('href', canonical)
      if (!document.querySelector('link[rel="canonical"]')) {
        document.head.appendChild(linkCanonical)
      }
    }

    // Set Open Graph tags
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: ogImage },
      { property: 'og:type', content: ogType },
      { property: 'og:site_name', content: 'SourcedIn - RFP Marketplace' }
    ]

    ogTags.forEach(tag => {
      const ogTag = document.querySelector(`meta[property="${tag.property}"]`) || document.createElement('meta')
      ogTag.setAttribute('property', tag.property)
      ogTag.setAttribute('content', tag.content)
      if (!document.querySelector(`meta[property="${tag.property}"]`)) {
        document.head.appendChild(ogTag)
      }
    })

    // Set Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage }
    ]

    twitterTags.forEach(tag => {
      const twitterTag = document.querySelector(`meta[name="${tag.name}"]`) || document.createElement('meta')
      twitterTag.setAttribute('name', tag.name)
      twitterTag.setAttribute('content', tag.content)
      if (!document.querySelector(`meta[name="${tag.name}"]`)) {
        document.head.appendChild(twitterTag)
      }
    })

    // Set robots meta tag
    const robotsTag = document.querySelector('meta[name="robots"]') || document.createElement('meta')
    robotsTag.setAttribute('name', 'robots')
    robotsTag.setAttribute('content', noIndex ? 'noindex, nofollow' : 'index, follow')
    if (!document.querySelector('meta[name="robots"]')) {
      document.head.appendChild(robotsTag)
    }

    // Add structured data
    if (structuredData) {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(structuredData)
      document.head.appendChild(script)
    }

    // Cleanup function
    return () => {
      // Remove structured data script on unmount
      if (structuredData) {
        const scripts = document.querySelectorAll('script[type="application/ld+json"]')
        scripts.forEach(script => {
          if (script.textContent === JSON.stringify(structuredData)) {
            script.remove()
          }
        })
      }
    }
  }, [title, description, keywords, canonical, ogImage, ogType, structuredData, noIndex])

  return null // This component doesn't render anything
}
